import { AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import '@/dashboard/i18n/index';
import '@/dashboard/injects/index';
import { muiTheme } from '@/dashboard/mui/theme';
import { globalDataStore } from '@/dashboard/store/globalDataStore';
import '@/dashboard/styles/globals.css';
import JoveAuthProvider from '@/dashboard/utils/JoveAuthProvider';
import { Auth0Provider } from '@auth0/auth0-react';
import { isLogoutSyncPagePath } from '@jove-frontend/uikit';
import { ThemeProvider } from '@mui/material/styles';
import * as Sentry from '@sentry/nextjs';
import { StateMachineProvider } from 'little-state-machine';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import 'react-calendar/dist/Calendar.css';
import { Toaster } from 'react-hot-toast';
import { AnalyticsProvider } from 'use-analytics';
import analytics from '../lib/analytics';

const Domain = process.env.NX_AUTH0_ISSUER_BASE_URL;
const ClientId = process.env.NX_AUTH0_CLIENT_ID;
const RedirectUrl = process.env.NX_AUTH0_REDIRECT_URL;

// Check that PostHog is client-side (used to handle Next.js SSR)
if (typeof window !== 'undefined') {
  posthog.init('phc_GWMy0ZDzSKuoYUOmo7411DMtmyehnTpx4uQFY4mHhLu', {
    api_host: '/ingest',
    ui_host: 'https://us.posthog.com',

    loaded: (posthog) => {
      // Disable in development and staging
      if (process.env.NODE_ENV === 'development' || process.env.STAGING === 'true') posthog.opt_out_capturing();
    },
  });
  // Disable in development and staging
  if (!(process.env.NODE_ENV === 'development' || process.env.STAGING === 'true')) {
    Sentry.addIntegration(
      new posthog.SentryIntegration(posthog, 'https://sentry.io/organizations/jove-insurance/projects/', 4504516236541952, undefined, [
        'error',
        'warning',
        'fatal',
      ])
    );
  }
}

function JoveDashboard({ Component, pageProps }: AppProps) {
  const router = useRouter();
  const {
    actions: { updateGlobalQueryParams },
  } = globalDataStore();

  useEffect(() => {
    if (isLogoutSyncPagePath(router.pathname)) return;

    makeRequests();
    // Track page views
    const handleRouteChange = () => posthog?.capture('$pageview');
    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, []);

  /**
   * methods
   */
  const makeRequests = () => {
    const { queryUserId } = router.query;
    if (queryUserId) {
      updateGlobalQueryParams({ queryUserId });
    }
  };

  if (isLogoutSyncPagePath(router.pathname)) {
    return <Component {...pageProps} />;
  }

  return (
    <>
      <Head>
        <title>Jove | Dashboard</title>
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
      </Head>
      <main className="app prose antialiased">
        <AnalyticsProvider instance={analytics}>
          {/* <Auth0Provider
            domain={Domain}
            clientId={ClientId}
            authorizationParams={{
              redirect_uri: RedirectUrl,
            }}
            onRedirectCallback={(appState, user) => {
              console.warn('onRedirectCallback: ', appState, user);
              router.replace(appState.returnTo);
            }}
          > */}
          <JoveAuthProvider>
            <StateMachineProvider>
              <PostHogProvider client={posthog}>
                <ThemeProvider theme={muiTheme}>
                  <Component {...pageProps} />
                </ThemeProvider>
              </PostHogProvider>
            </StateMachineProvider>
          </JoveAuthProvider>
          {/* </Auth0Provider> */}
        </AnalyticsProvider>
        <Toaster
          toastOptions={{
            duration: 3000,
          }}
        />
      </main>
    </>
  );
}

export default JoveDashboard;
